import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const pad = (value) => String(value).padStart(2, "0");

const formatDateInput = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const formatNumber = (value, decimals = 2) =>
  Number(value || 0).toLocaleString("it-IT", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const formatInteger = (value) =>
  Number(value || 0).toLocaleString("it-IT", {
    maximumFractionDigits: 0,
  });

const formatDateItalian = (value) => {
  if (!value) return "-";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
};

const formatMonth = (value) => {
  if (!value) return "";
  const [year, month] = value.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);

  return date.toLocaleDateString("it-IT", {
    month: "short",
    year: "numeric",
  });
};

function getYearPeriod() {
  const today = new Date();

  return {
    start: `${today.getFullYear()}-01-01`,
    end: formatDateInput(today),
  };
}

function getMonthPeriod() {
  const today = new Date();

  return {
    start: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-01`,
    end: formatDateInput(today),
  };
}

function StatisticheFidelityPage() {
  const navigate = useNavigate();

  const initialPeriod = useMemo(() => getYearPeriod(), []);

  const [startDate, setStartDate] = useState(initialPeriod.start);
  const [endDate, setEndDate] = useState(initialPeriod.end);
  const [activePreset, setActivePreset] = useState("year");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadStatistics = async (start = startDate, end = endDate) => {
    if (!start || !end) {
      setError("Seleziona una data iniziale e una data finale.");
      return;
    }

    if (start > end) {
      setError("La data iniziale non può essere successiva alla data finale.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await authFetch(
        `/api/rewards/statistics/?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
      );

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(
          payload?.detail || "Impossibile caricare le statistiche Fidelity."
        );
      }

      setData(payload);
    } catch (err) {
      setError(
        err?.message || "Impossibile caricare le statistiche Fidelity."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatistics(initialPeriod.start, initialPeriod.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectYear = () => {
    const period = getYearPeriod();
    setStartDate(period.start);
    setEndDate(period.end);
    setActivePreset("year");
    loadStatistics(period.start, period.end);
  };

  const selectMonth = () => {
    const period = getMonthPeriod();
    setStartDate(period.start);
    setEndDate(period.end);
    setActivePreset("month");
    loadStatistics(period.start, period.end);
  };

  const applyCustomPeriod = () => {
    setActivePreset("custom");
    loadStatistics(startDate, endDate);
  };

  const current = data?.current || {};
  const previous = data?.previous || {};
  const comparison = data?.comparison || {};

  const comparisonText = (value) => {
    if (value === null || value === undefined) {
      return "Nessun dato nel periodo precedente";
    }

    const number = Number(value);
    const prefix = number > 0 ? "+" : "";

    return `${prefix}${formatNumber(number, 1)}% rispetto all'anno precedente`;
  };

  const comparisonClass = (value) => {
    if (value === null || value === undefined || Number(value) === 0) {
      return "neutral";
    }

    return Number(value) > 0 ? "positive" : "negative";
  };

  const maxTrendValue = Math.max(
    1,
    ...(current.trend || []).flatMap((row) => [
      Number(row.earned_points || 0),
      Number(row.spent_points || 0),
    ])
  );

  const kpis = [
    {
      label: "Punti maturati",
      value: formatNumber(current.earned_points),
      note: comparisonText(comparison.earned_points_pct),
      comparison: comparison.earned_points_pct,
    },
    {
      label: "Punti usati per premi",
      value: formatNumber(current.spent_points),
      note: comparisonText(comparison.spent_points_pct),
      comparison: comparison.spent_points_pct,
    },
    {
      label: "Premi consegnati",
      value: formatInteger(current.rewards_delivered),
      note: comparisonText(comparison.rewards_delivered_pct),
      comparison: comparison.rewards_delivered_pct,
    },
    {
      label: "Clienti premiati",
      value: formatInteger(current.unique_clients),
      note: comparisonText(comparison.unique_clients_pct),
      comparison: comparison.unique_clients_pct,
    },
    {
      label: "Costo premi",
      value: `EUR ${formatNumber(current.rewards_cost)}`,
      note: comparisonText(comparison.rewards_cost_pct),
      comparison: comparison.rewards_cost_pct,
    },
  ];

  return (
    <div className="fidelity-stats-page">
      <style>{`
        .fidelity-stats-page {
          min-height: 100vh;
          background: #f5f7fb;
          padding: 24px;
          color: #172033;
        }

        .fidelity-stats-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .fidelity-stats-topbar {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 20px;
          margin-bottom: 22px;
        }

        .fidelity-stats-back {
          border: 0;
          background: #ffffff;
          color: #26334d;
          padding: 11px 16px;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(23, 32, 51, 0.08);
        }

        .fidelity-stats-title {
          margin: 0;
          font-size: 30px;
          line-height: 1.15;
        }

        .fidelity-stats-subtitle {
          margin: 7px 0 0;
          color: #6c7689;
          line-height: 1.5;
        }

        .fidelity-panel {
          background: #ffffff;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 4px 18px rgba(23, 32, 51, 0.07);
          margin-bottom: 20px;
        }

        .fidelity-period-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 16px;
        }

        .fidelity-period-button {
          border: 1px solid #d8deea;
          background: #ffffff;
          color: #26334d;
          border-radius: 10px;
          padding: 10px 15px;
          cursor: pointer;
          font-weight: 700;
        }

        .fidelity-period-button.active {
          background: #172033;
          border-color: #172033;
          color: #ffffff;
        }

        .fidelity-date-row {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          gap: 12px;
        }

        .fidelity-date-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .fidelity-date-field label {
          font-size: 13px;
          color: #6c7689;
          font-weight: 700;
        }

        .fidelity-date-field input {
          border: 1px solid #d8deea;
          border-radius: 9px;
          padding: 10px 12px;
          font-size: 15px;
          background: #ffffff;
        }

        .fidelity-apply {
          border: 0;
          border-radius: 9px;
          padding: 11px 18px;
          background: #2367d1;
          color: #ffffff;
          font-weight: 800;
          cursor: pointer;
        }

        .fidelity-period-summary {
          margin-top: 15px;
          color: #5e687a;
          font-size: 14px;
        }

        .fidelity-error {
          background: #fff0f0;
          color: #a72828;
          border: 1px solid #f0c6c6;
          padding: 14px 16px;
          border-radius: 12px;
          margin-bottom: 18px;
          font-weight: 700;
        }

        .fidelity-loading {
          text-align: center;
          padding: 50px 20px;
          color: #6c7689;
          font-weight: 700;
        }

        .fidelity-kpi-grid {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 14px;
          margin-bottom: 20px;
        }

        .fidelity-kpi {
          background: #ffffff;
          border-radius: 15px;
          padding: 18px;
          box-shadow: 0 4px 18px rgba(23, 32, 51, 0.07);
          min-width: 0;
        }

        .fidelity-kpi-label {
          color: #6c7689;
          font-size: 13px;
          font-weight: 800;
          margin-bottom: 8px;
        }

        .fidelity-kpi-value {
          font-size: 27px;
          line-height: 1.15;
          font-weight: 900;
          color: #172033;
          overflow-wrap: anywhere;
        }

        .fidelity-kpi-note {
          margin-top: 9px;
          font-size: 12px;
          line-height: 1.35;
          font-weight: 700;
        }

        .fidelity-kpi-note.positive {
          color: #16804a;
        }

        .fidelity-kpi-note.negative {
          color: #b33636;
        }

        .fidelity-kpi-note.neutral {
          color: #8790a0;
        }

        .fidelity-migration-box {
          display: flex;
          justify-content: space-between;
          gap: 20px;
          align-items: center;
          background: #fffaf0;
          border: 1px solid #f0dfb5;
          border-radius: 14px;
          padding: 17px 19px;
          margin-bottom: 20px;
        }

        .fidelity-migration-title {
          font-weight: 900;
          margin-bottom: 4px;
        }

        .fidelity-migration-text {
          color: #756b57;
          font-size: 13px;
          line-height: 1.4;
        }

        .fidelity-migration-value {
          font-size: 25px;
          font-weight: 900;
          white-space: nowrap;
        }

        .fidelity-two-columns {
          display: grid;
          grid-template-columns: 1.35fr 1fr;
          gap: 20px;
          margin-bottom: 20px;
        }

        .fidelity-section-title {
          margin: 0 0 5px;
          font-size: 19px;
        }

        .fidelity-section-description {
          margin: 0 0 18px;
          color: #778195;
          font-size: 13px;
          line-height: 1.45;
        }

        .fidelity-trend-empty {
          padding: 30px 10px;
          text-align: center;
          color: #8a93a3;
        }

        .fidelity-trend-row {
          display: grid;
          grid-template-columns: 85px 1fr;
          gap: 12px;
          align-items: center;
          margin-bottom: 14px;
        }

        .fidelity-trend-month {
          font-size: 13px;
          font-weight: 800;
          text-transform: capitalize;
        }

        .fidelity-bars {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .fidelity-bar-line {
          display: grid;
          grid-template-columns: 1fr 90px;
          gap: 10px;
          align-items: center;
        }

        .fidelity-bar-track {
          height: 9px;
          background: #edf0f5;
          border-radius: 999px;
          overflow: hidden;
        }

        .fidelity-bar {
          height: 100%;
          border-radius: 999px;
          min-width: 0;
        }

        .fidelity-bar.earned {
          background: #2367d1;
        }

        .fidelity-bar.spent {
          background: #d45a5a;
        }

        .fidelity-bar-value {
          font-size: 12px;
          color: #667085;
          text-align: right;
          white-space: nowrap;
        }

        .fidelity-legend {
          display: flex;
          gap: 18px;
          flex-wrap: wrap;
          margin-top: 15px;
          color: #667085;
          font-size: 12px;
          font-weight: 700;
        }

        .fidelity-legend span::before {
          content: "";
          display: inline-block;
          width: 9px;
          height: 9px;
          border-radius: 50%;
          margin-right: 6px;
        }

        .fidelity-legend .earned::before {
          background: #2367d1;
        }

        .fidelity-legend .spent::before {
          background: #d45a5a;
        }

        .fidelity-comparison-table,
        .fidelity-ranking-table {
          width: 100%;
          border-collapse: collapse;
        }

        .fidelity-comparison-table th,
        .fidelity-comparison-table td,
        .fidelity-ranking-table th,
        .fidelity-ranking-table td {
          text-align: left;
          padding: 11px 8px;
          border-bottom: 1px solid #edf0f5;
          font-size: 13px;
        }

        .fidelity-comparison-table th,
        .fidelity-ranking-table th {
          color: #7a8496;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .fidelity-comparison-table td:last-child,
        .fidelity-comparison-table th:last-child,
        .fidelity-ranking-table td:last-child,
        .fidelity-ranking-table th:last-child {
          text-align: right;
        }

        .fidelity-ranking-empty {
          color: #8a93a3;
          padding: 20px 0;
          text-align: center;
        }

        .fidelity-ranking-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .fidelity-note {
          margin-top: 20px;
          font-size: 12px;
          color: #8a93a3;
          text-align: center;
        }

        @media (max-width: 1050px) {
          .fidelity-kpi-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .fidelity-two-columns {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 720px) {
          .fidelity-stats-page {
            padding: 14px;
          }

          .fidelity-stats-topbar {
            flex-direction: column-reverse;
          }

          .fidelity-stats-title {
            font-size: 25px;
          }

          .fidelity-kpi-grid {
            grid-template-columns: 1fr 1fr;
          }

          .fidelity-ranking-grid {
            grid-template-columns: 1fr;
          }

          .fidelity-migration-box {
            align-items: flex-start;
            flex-direction: column;
          }
        }

        @media (max-width: 460px) {
          .fidelity-kpi-grid {
            grid-template-columns: 1fr;
          }

          .fidelity-date-field {
            width: 100%;
          }

          .fidelity-date-field input {
            width: 100%;
            box-sizing: border-box;
          }

          .fidelity-apply {
            width: 100%;
          }

          .fidelity-trend-row {
            grid-template-columns: 65px 1fr;
          }

          .fidelity-bar-line {
            grid-template-columns: 1fr 75px;
          }
        }
      `}</style>

      <div className="fidelity-stats-container">
        <div className="fidelity-stats-topbar">
          <div>
            <h1 className="fidelity-stats-title">Statistiche Fidelity</h1>
            <p className="fidelity-stats-subtitle">
              Analisi dei punti maturati, premi consegnati e costi del programma
              fedeltà.
            </p>
          </div>

          <button
            type="button"
            className="fidelity-stats-back"
            onClick={() => navigate("/gestione-app-clienti")}
          >
            Torna a Gestione App
          </button>
        </div>

        <div className="fidelity-panel">
          <div className="fidelity-period-buttons">
            <button
              type="button"
              className={`fidelity-period-button ${
                activePreset === "year" ? "active" : ""
              }`}
              onClick={selectYear}
            >
              Quest'anno
            </button>

            <button
              type="button"
              className={`fidelity-period-button ${
                activePreset === "month" ? "active" : ""
              }`}
              onClick={selectMonth}
            >
              Questo mese
            </button>
          </div>

          <div className="fidelity-date-row">
            <div className="fidelity-date-field">
              <label htmlFor="fidelity-start">Dal</label>
              <input
                id="fidelity-start"
                type="date"
                value={startDate}
                onChange={(event) => {
                  setStartDate(event.target.value);
                  setActivePreset("custom");
                }}
              />
            </div>

            <div className="fidelity-date-field">
              <label htmlFor="fidelity-end">Al</label>
              <input
                id="fidelity-end"
                type="date"
                value={endDate}
                onChange={(event) => {
                  setEndDate(event.target.value);
                  setActivePreset("custom");
                }}
              />
            </div>

            <button
              type="button"
              className="fidelity-apply"
              onClick={applyCustomPeriod}
            >
              Applica periodo
            </button>
          </div>

          {data?.period && (
            <div className="fidelity-period-summary">
              Periodo analizzato:{" "}
              <strong>{formatDateItalian(data.period.start)}</strong> -{" "}
              <strong>{formatDateItalian(data.period.end)}</strong>
              {" | "}
              Confronto:{" "}
              <strong>{formatDateItalian(data.previous_period.start)}</strong> -{" "}
              <strong>{formatDateItalian(data.previous_period.end)}</strong>
            </div>
          )}
        </div>

        {error && <div className="fidelity-error">{error}</div>}

        {loading ? (
          <div className="fidelity-panel fidelity-loading">
            Caricamento statistiche...
          </div>
        ) : (
          data && (
            <>
              <div className="fidelity-kpi-grid">
                {kpis.map((kpi) => (
                  <div className="fidelity-kpi" key={kpi.label}>
                    <div className="fidelity-kpi-label">{kpi.label}</div>
                    <div className="fidelity-kpi-value">{kpi.value}</div>
                    <div
                      className={`fidelity-kpi-note ${comparisonClass(
                        kpi.comparison
                      )}`}
                    >
                      {kpi.note}
                    </div>
                  </div>
                ))}
              </div>

              <div className="fidelity-migration-box">
                <div>
                  <div className="fidelity-migration-title">
                    Punti migrati dalle vecchie tessere
                  </div>
                  <div className="fidelity-migration-text">
                    Sono mostrati separatamente perché rappresentano punti già
                    posseduti dai clienti e non nuova attività del periodo.
                  </div>
                </div>

                <div className="fidelity-migration-value">
                  {formatNumber(current.migrated_points)} pt
                </div>
              </div>

              <div className="fidelity-two-columns">
                <div className="fidelity-panel">
                  <h2 className="fidelity-section-title">
                    Andamento punti
                  </h2>
                  <p className="fidelity-section-description">
                    Punti maturati e punti utilizzati per i premi mese per mese.
                  </p>

                  {(current.trend || []).length === 0 ? (
                    <div className="fidelity-trend-empty">
                      Nessun movimento nel periodo selezionato.
                    </div>
                  ) : (
                    <>
                      {current.trend.map((row) => (
                        <div className="fidelity-trend-row" key={row.month}>
                          <div className="fidelity-trend-month">
                            {formatMonth(row.month)}
                          </div>

                          <div className="fidelity-bars">
                            <div className="fidelity-bar-line">
                              <div className="fidelity-bar-track">
                                <div
                                  className="fidelity-bar earned"
                                  style={{
                                    width: `${
                                      (Number(row.earned_points || 0) /
                                        maxTrendValue) *
                                      100
                                    }%`,
                                  }}
                                />
                              </div>
                              <div className="fidelity-bar-value">
                                +{formatNumber(row.earned_points)} pt
                              </div>
                            </div>

                            <div className="fidelity-bar-line">
                              <div className="fidelity-bar-track">
                                <div
                                  className="fidelity-bar spent"
                                  style={{
                                    width: `${
                                      (Number(row.spent_points || 0) /
                                        maxTrendValue) *
                                      100
                                    }%`,
                                  }}
                                />
                              </div>
                              <div className="fidelity-bar-value">
                                -{formatNumber(row.spent_points)} pt
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      <div className="fidelity-legend">
                        <span className="earned">Punti maturati</span>
                        <span className="spent">Punti usati per premi</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="fidelity-panel">
                  <h2 className="fidelity-section-title">
                    Confronto anno precedente
                  </h2>
                  <p className="fidelity-section-description">
                    Stesso intervallo di date dell'anno precedente.
                  </p>

                  <table className="fidelity-comparison-table">
                    <thead>
                      <tr>
                        <th>Indicatore</th>
                        <th>Attuale</th>
                        <th>Precedente</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Punti maturati</td>
                        <td>{formatNumber(current.earned_points)}</td>
                        <td>{formatNumber(previous.earned_points)}</td>
                      </tr>
                      <tr>
                        <td>Punti usati</td>
                        <td>{formatNumber(current.spent_points)}</td>
                        <td>{formatNumber(previous.spent_points)}</td>
                      </tr>
                      <tr>
                        <td>Premi consegnati</td>
                        <td>{formatInteger(current.rewards_delivered)}</td>
                        <td>{formatInteger(previous.rewards_delivered)}</td>
                      </tr>
                      <tr>
                        <td>Clienti premiati</td>
                        <td>{formatInteger(current.unique_clients)}</td>
                        <td>{formatInteger(previous.unique_clients)}</td>
                      </tr>
                      <tr>
                        <td>Costo premi</td>
                        <td>EUR {formatNumber(current.rewards_cost)}</td>
                        <td>EUR {formatNumber(previous.rewards_cost)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="fidelity-ranking-grid">
                <div className="fidelity-panel">
                  <h2 className="fidelity-section-title">
                    Premi più consegnati
                  </h2>
                  <p className="fidelity-section-description">
                    Classifica dei premi realmente ritirati nel periodo.
                  </p>

                  {(current.reward_ranking || []).length === 0 ? (
                    <div className="fidelity-ranking-empty">
                      Nessun premio consegnato nel periodo.
                    </div>
                  ) : (
                    <table className="fidelity-ranking-table">
                      <thead>
                        <tr>
                          <th>Premio</th>
                          <th>Quantità</th>
                          <th>Costo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {current.reward_ranking.map((row, index) => (
                          <tr key={`${row.reward_name}-${index}`}>
                            <td>{row.reward_name}</td>
                            <td>{formatInteger(row.quantity)}</td>
                            <td>EUR {formatNumber(row.cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="fidelity-panel">
                  <h2 className="fidelity-section-title">
                    Prodotti premio più consegnati
                  </h2>
                  <p className="fidelity-section-description">
                    Prodotti effettivamente scelti e consegnati ai clienti.
                  </p>

                  {(current.product_ranking || []).length === 0 ? (
                    <div className="fidelity-ranking-empty">
                      Nessun prodotto premio consegnato nel periodo.
                    </div>
                  ) : (
                    <table className="fidelity-ranking-table">
                      <thead>
                        <tr>
                          <th>Prodotto</th>
                          <th>Quantità</th>
                          <th>Costo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {current.product_ranking.map((row, index) => (
                          <tr key={`${row.product_name}-${index}`}>
                            <td>{row.product_name}</td>
                            <td>{formatInteger(row.quantity)}</td>
                            <td>EUR {formatNumber(row.cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="fidelity-note">
                Le statistiche escludono automaticamente l'account utilizzato
                per i test del gestionale.
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}

export default StatisticheFidelityPage;
